import { ELEMENT_CONTRACT_ADDRESS } from '@components/RichTextPlate/plugins/createContractAddressPlugin'
import { ELEMENT_MENTION_TICKER } from '@components/RichTextPlate/plugins/createTickerMentionPlugin'
import { getChainFromAddress } from '@components/RichTextPlate/plugins/PasteContractAddressPlugin'
import { ElementOrTextOf, TEditor } from '@udecode/plate-common'
import { MentionPlugin } from '@udecode/plate-mention/react'
import { each, isEmpty, pick } from 'lodash-es'
import { findAndReplace } from 'mdast-util-find-and-replace'
import { Transformer } from 'unified'
import { Channel, useUserLookupContext } from 'use-towns-client'
import { getPrettyDisplayName } from 'utils/getPrettyDisplayName'

import {
    AtChannelUser,
    TChannelMentionElement,
    TTickerMentionElement,
    TUserIDNameMap,
    TUserMentionElement,
} from '../../components/plate-ui/autocomplete/types'
import { ELEMENT_MENTION_CHANNEL } from '../../plugins/createChannelPlugin'

const SPACE_NODE = {
    type: 'text',
    value: ' ',
}

const USER_TRIGGER = '@'
export const USER_ADDRESS_MENTION_REGEX = new RegExp(
    `(<${USER_TRIGGER}(0x[a-fA-F0-9]{40})>|${USER_TRIGGER}(0x[a-fA-F0-9]{40})(?=\\s|[^a-z0-9_-]|$))`,
    'ig',
)

const userNameWithoutAt = (name: string) => name.replace(/^@/, '')
export type PasteTransformer = (fragment: ElementOrTextOf<TEditor>[]) => ElementOrTextOf<TEditor>[]
/**
 * Find #channel and @user in Markdown AST and convert them to
 * `TChannelMentionElement` or `TUserMentionElement` respectively.
 * It also has a special rule for @0xUserId format where userId is an Ethereum address, converting it to `TUserMentionElement`.
 *
 * @param channelList - List of channels in the current space
 * @param userHashMap - List of mentions attached to message event, which means how many users are actually mentioned in
 * the message and their details DURING the time of message creation
 * @param lookupUser - a method to lookup user by userId
 *
 * @description 1. add special mention for `@channel` because the data type returned from backend has no
 * displayName/userId for it
 *
 * 2. create regex pattern for all user mentions by their display names.
 *
 * 3. We also create a map of user display names to their userIds and override the old displayName with the new one if it exists in the users list.
 *
 * 4. Loop through the tree and replace the user and channel mentions with the corresponding elements.
 *
 * @see TChannelMentionElement
 * @see TUserMention
 */
function remarkTransformUserAndChannels(
    channelList: Channel[],
    userHashMap: TUserIDNameMap,
    lookupUser?: ReturnType<typeof useUserLookupContext>['lookupUser'],
): (onPaste?: boolean) => Transformer

function remarkTransformUserAndChannels(
    channelList: Channel[],
    userHashMap: TUserIDNameMap,
    lookupUser?: ReturnType<typeof useUserLookupContext>['lookupUser'],
): (onPaste?: boolean) => PasteTransformer

function remarkTransformUserAndChannels(
    channelList: Channel[],
    userHashMap: TUserIDNameMap = {},
    lookupUser?: ReturnType<typeof useUserLookupContext>['lookupUser'],
) {
    return function (onPaste?: boolean) {
        const CHANNEL_TRIGGER = '#'
        // We allow channel names to have emoji characters, hence we use \p{Extended_Pictographic} instead of \w
        const CHANNEL_NAME_REGEX =
            '[-\\da-z_<a?:.+?:\\d{18}>\\p{Extended_Pictographic}\\uFE0F]{0,50}'

        const CHANNEL_ELEMENT_REGEX = new RegExp(
            `(?:^|\\s)${CHANNEL_TRIGGER}(${CHANNEL_NAME_REGEX})`,
            'gui',
        )

        const TICKER_TRIGGER = '$'
        // require the ticker to start with at least one letter
        const TICKER_NAME_REGEX = '[a-z][-a-z0-9]*'
        const TICKER_ELEMENT_REGEX = new RegExp(
            `(?:^|\\s)\\${TICKER_TRIGGER}(${TICKER_NAME_REGEX})`,
            'gui',
        )

        const CONTRACT_ADDRESS_ELEMENT_REGEX = new RegExp(
            `(?<=^|\\s)(0x[a-fA-F0-9]{40}|[1-9A-HJ-NP-Za-km-z]{32,44})(?=\\s|$)`,
            'gui',
        )

        const userIdList = Object.values(userHashMap)
        const USER_NAME_REGEX = userIdList
            .concat(AtChannelUser.displayName)
            .map((userDisplayName) =>
                userNameWithoutAt(userDisplayName).replace(/[/\-\\^$*+?.()|[\]{}]/g, '\\$&'),
            )
            .join('|')

        const userIdNameCurrent = {
            [AtChannelUser.displayName]: pick(AtChannelUser, 'userId', 'displayName'),
        }

        each(userHashMap, (displayName, userId) => {
            const mentionDisplayName = userNameWithoutAt(displayName)
            const member = lookupUser?.(userId)
            userIdNameCurrent[mentionDisplayName] = {
                userId,
                displayName: !isEmpty(member) ? getPrettyDisplayName(member) : mentionDisplayName,
            }
        })

        const USER_ELEMENT_REGEX = new RegExp(
            `(${USER_TRIGGER}(${USER_NAME_REGEX})(?=\\s|[^a-z0-9_-]|$))`,
            'ig',
        )

        const transformUser = (value: string, selection: string) => {
            const whitespace = []
            if (value.indexOf(USER_TRIGGER) > 0) {
                whitespace.push({
                    type: 'text',
                    value: value.substring(0, value.indexOf(USER_TRIGGER)),
                })
            }
            const userName = value.split(USER_TRIGGER)[1]
            const user = userIdNameCurrent[userName]

            if (!user) {
                return [
                    {
                        type: 'text',
                        value,
                    },
                ]
            }

            return [
                ...whitespace,
                {
                    userId: user.userId,
                    atChannel: userName === AtChannelUser.displayName,
                    value: USER_TRIGGER + user.displayName,
                    type: MentionPlugin.key,
                    children: [{ text: USER_TRIGGER + user.displayName }],
                } as TUserMentionElement,
            ]
        }

        const transformUserAddressMention = (value: string) => {
            const match = value.match(/<@(0x[a-fA-F0-9]{40})>|@(0x[a-fA-F0-9]{40})/)
            if (!match) {
                return [{ type: 'text', value }]
            }
            // userId is in capture group 1 for <@...> format, group 2 for @... format
            const userId = match[1] || match[2]
            const displayName = getPrettyDisplayName(lookupUser?.(userId))

            return [
                {
                    userId,
                    value: `@${displayName}`,
                    type: MentionPlugin.key,
                    children: [{ text: `@${displayName}` }],
                } as TUserMentionElement,
            ]
        }

        const transformChannel = (value: string, selection: string) => {
            const whitespace = []
            if (value.indexOf(CHANNEL_TRIGGER) > 0) {
                whitespace.push({
                    type: 'text',
                    value: value.substring(0, value.indexOf(CHANNEL_TRIGGER)),
                })
            }
            const channelName = value.split(CHANNEL_TRIGGER)[1]
            const channel = channelList.find((c) => c.label === channelName)
            if (!channel) {
                return [
                    {
                        type: 'text',
                        value,
                    },
                ]
            }

            return [
                ...whitespace,
                {
                    type: ELEMENT_MENTION_CHANNEL,
                    channel,
                    value,
                    children: [{ text: value }],
                } as TChannelMentionElement,
                { ...SPACE_NODE },
            ]
        }

        const transformTicker = (value: string, selection: string) => {
            const whitespace = []
            if (value.indexOf(TICKER_TRIGGER) > 0) {
                whitespace.push({
                    type: 'text',
                    value: value.substring(0, value.indexOf(TICKER_TRIGGER)),
                })
            }
            const tickerName = value.split(TICKER_TRIGGER)[1]
            const ticker = { name: tickerName, symbol: tickerName }

            return [
                ...whitespace,
                {
                    type: ELEMENT_MENTION_TICKER,
                    ticker,
                    value,
                    children: [{ text: value }],
                } as TTickerMentionElement,
                { ...SPACE_NODE },
            ]
        }

        const transformContractAddress = (value: string) => {
            const address = value.trim()
            const chain = getChainFromAddress(value)
            if (chain) {
                return [
                    {
                        type: ELEMENT_CONTRACT_ADDRESS,
                        value: address,
                        address,
                        chain,
                        children: [{ text: address }],
                    },
                ]
            } else {
                return [
                    {
                        type: 'text',
                        value,
                    },
                ]
            }
        }

        type SanitizeFragmentType = {
            (mentionFragment: ReturnType<typeof transformChannel>): ElementOrTextOf<TEditor>
            (mentionFragment: ReturnType<typeof transformUser>): ElementOrTextOf<TEditor>
            (mentionFragment: ReturnType<typeof transformTicker>): ElementOrTextOf<TEditor>
            (mentionFragment: ReturnType<typeof transformContractAddress>): ElementOrTextOf<TEditor>
        }
        const sanitizeFragment: SanitizeFragmentType = (mentionFragment) => {
            if (mentionFragment.length === 1) {
                return { text: mentionFragment[0].value }
            }

            return mentionFragment.filter(
                (f) => f.type === MentionPlugin.key || f.type === ELEMENT_MENTION_CHANNEL,
            )[0] as ElementOrTextOf<TEditor>
        }

        const recursivelyTransformMentions = (fragment: ElementOrTextOf<TEditor>) => {
            if (Array.isArray(fragment.children)) {
                fragment.children = fragment.children.map(recursivelyTransformMentions)
            }

            if (!isEmpty(fragment.text) && typeof fragment.text === 'string') {
                if (fragment.text.match(USER_ELEMENT_REGEX)) {
                    fragment = sanitizeFragment(transformUser(fragment.text, ''))
                }
            }

            return fragment
        }

        /**
         * Transformer to find and replace user and channel mentions in the markdown AST using UnifiedJS
         * This is used to convert mentions in Timeline preview and during initial load of the editor from local storage
         * Called from `deserializeMd` in `utils/deserializeMD.ts` and `MarkdownToJSX` in `components/MarkdownToJSX.tsx`
         */
        const transformer: Transformer = (tree, _file) => {
            // eslint-disable-next-line
            // @ts-expect-error: replacing ts-ignore
            findAndReplace(tree, [
                [USER_ADDRESS_MENTION_REGEX, transformUserAddressMention],
                [CHANNEL_ELEMENT_REGEX, transformChannel],
                [USER_ELEMENT_REGEX, transformUser],
                [TICKER_ELEMENT_REGEX, transformTicker],
                [CONTRACT_ADDRESS_ELEMENT_REGEX, transformContractAddress],
            ])
        }

        /**
         * Transformer to find and replace user and channel mentions in the Plate JS paste fragment manually
         * This is used to convert mentions when copied from other sources and pasted in the editor
         * Called from `createPasteMentionsPlugin` in `plugins/createPasteMentionsPlugin.ts`
         */
        const pasteTransformer: PasteTransformer = (fragment) => {
            if (!Array.isArray(fragment)) {
                return fragment
            }
            return fragment.map(recursivelyTransformMentions)
        }

        return onPaste ? pasteTransformer : transformer
    }
}

export default remarkTransformUserAndChannels
